import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function CompanyLoginSecurity({ enabled, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!["admin", "super_admin"].includes(user?.role)) return null;
  const change = async value => {
    setBusy(true);
    try {
      await api.patch("/org/me", { enhanced_login_security: value });
      onChanged(value);
      toast.success(value ? "Enhanced Login Security enabled" : "Enhanced Login Security disabled");
    } catch { toast.error("Could not update security settings. Please try again."); }
    finally { setBusy(false); }
  };
  return <section className="hard-card p-5 sm:p-6">
    <div className="label-tag mb-3">Security</div>
    <h2 className="font-semibold text-lg">Enhanced Login Security</h2>
    <p className="text-sm text-muted-foreground mt-2">Protect administrator and office accounts when signing in from a new device.</p>
    <label className="flex items-center justify-between gap-4 mt-5"><span className="text-sm font-medium">Require email verification on new devices</span><Switch checked={!!enabled} disabled={busy} onCheckedChange={change} aria-label="Require email verification on new devices" /></label>
    <p className="text-xs text-muted-foreground mt-3">Applies to Administrators and Office users. Fieldworkers are not affected. Existing signed-in sessions continue normally.</p>
  </section>;
}

export function TrustedDevices() {
  const { user } = useAuth();
  const eligible = ["admin", "office"].includes(user?.role);
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/auth/device/trusted"); setDevices(data); setError(false); }
    catch { setError(true); }
  };
  useEffect(() => { if (eligible) load(); }, [user?.id, user?.org_id, eligible]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!eligible) return null;
  const remove = async id => {
    if (!window.confirm(id === "all" ? "Remove all trusted devices? This browser will need email verification on its next sign-in when Enhanced Login Security is enabled." : "Remove this trusted device? Its next sign-in will require email verification when Enhanced Login Security is enabled.")) return;
    setBusy(true);
    try { await api.delete(`/auth/device/trusted/${id}`); await load(); toast.success("Device trust removed"); }
    catch { toast.error("Could not remove device trust. Please try again."); }
    finally { setBusy(false); }
  };
  return <section className="hard-card p-5">
    <h2 className="label-tag mb-3">Trusted devices</h2>
    <p className="text-sm text-muted-foreground">Browsers you have verified by email. Removing trust affects future sign-ins, not existing sessions.</p>
    {error ? <Button variant="outline" onClick={load} className="mt-3">Retry loading devices</Button> : devices === null ? <p className="mt-3 text-sm">Loading…</p> : <>
      {devices.length === 0 && <p className="mt-3 text-sm">No trusted browsers.</p>}
      <ul className="divide-y mt-3">{devices.map(device => <li key={device.id} className="py-3 flex items-center justify-between gap-3">
        <div><p className="text-sm font-semibold">{device.device_label}{device.current && " · Current browser"}</p><p className="text-xs text-muted-foreground">Last used {new Date(device.last_used_at).toLocaleDateString()} · Trust expires {new Date(device.expires_at).toLocaleDateString()}</p></div>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => remove(device.id)}>Remove</Button>
      </li>)}</ul>
      {!!devices.length && <Button variant="outline" disabled={busy} onClick={() => remove("all")} className="mt-3">Remove all trusted devices</Button>}
    </>}
  </section>;
}
